import type { Prototype } from '../prototypes/types';

/**
 * Installation and offline storage, for section 6 of the exploration plan.
 *
 * Section 6.1 asks that offline state be *visible* rather than invisible - the
 * question is not only whether caching works, but whether a teacher can tell
 * what the device has kept. So each prototype is a "lesson pack" a teacher
 * chooses to store, and its status is reported honestly, including the awkward
 * case where a new build has left a previously-stored pack stale.
 */

const PACK_STORAGE_PREFIX = 'ifp.pack.';
/**
 * Small grace period before reading the cache back. The worker awaits each
 * write before responding, so this only covers the tail of a module graph
 * rather than papering over a race.
 */
const CACHE_SETTLE_MS = 120;

export type PackStatus = 'unavailable' | 'ready' | 'stale' | 'downloading';

export interface PackState {
  id: string;
  status: PackStatus;
}

const storageKey = (id: string) => `${PACK_STORAGE_PREFIX}${id}`;

function readPackUrls(id: string): string[] {
  try {
    const raw = localStorage.getItem(storageKey(id));
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : [];
  } catch {
    // Private mode, cleared storage, or corrupt JSON. Treat as not downloaded
    // rather than failing the whole screen.
    return [];
  }
}

function writePackUrls(id: string, urls: string[]): void {
  try {
    localStorage.setItem(storageKey(id), JSON.stringify(urls));
  } catch {
    // Storage full or blocked. The pack is still cached and playable this
    // session; we simply cannot report it as stored next time.
  }
}

export function serviceWorkerSupported(): boolean {
  return 'serviceWorker' in navigator && 'caches' in window;
}

export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  // Dev has no generated worker, and registering one there fights Vite's
  // module graph. Offline is a production-build behaviour; test with
  // `pnpm build && pnpm preview`.
  if (!import.meta.env.PROD || !serviceWorkerSupported()) return null;

  try {
    const registration = await navigator.serviceWorker.register('./sw.js', { scope: './' });
    await navigator.serviceWorker.ready;
    return registration;
  } catch {
    return null;
  }
}

/** Every URL currently held in any of our caches. */
async function cachedUrls(): Promise<Set<string>> {
  const urls = new Set<string>();
  for (const name of await caches.keys()) {
    if (!name.startsWith('ifp-')) continue;
    const cache = await caches.open(name);
    for (const request of await cache.keys()) urls.add(request.url);
  }
  return urls;
}

/**
 * Fetch a prototype's code and record which URLs it pulled in, so its offline
 * status can later be verified rather than merely asserted.
 *
 * Works by diffing the cache around the import. An earlier version watched
 * PerformanceObserver resource entries instead, which reported nothing while
 * the files were demonstrably being cached - and diffing the cache is the more
 * direct question anyway, since what we want to know is what is *stored*, not
 * what was requested.
 *
 * The shared engine chunk is attributed to whichever pack pulls it in first.
 * That is honest: it is the first pack that made the device pay for it, and
 * packs are only ever cleared all together.
 */
export async function downloadPack(prototype: Prototype): Promise<PackState> {
  if (!prototype.load) return { id: prototype.id, status: 'unavailable' };

  const before = await cachedUrls();
  await prototype.load();
  await new Promise((resolve) => setTimeout(resolve, CACHE_SETTLE_MS));
  const after = await cachedUrls();

  const added = [...after].filter((url) => !before.has(url));

  // A pack already loaded this session adds nothing new, so keep whatever was
  // recorded before rather than overwriting it with an empty list.
  const urls = added.length > 0 ? added : readPackUrls(prototype.id);
  if (added.length > 0) writePackUrls(prototype.id, urls);

  return packState(prototype.id, urls);
}

async function packState(id: string, urls: string[]): Promise<PackState> {
  if (urls.length === 0) return { id, status: 'unavailable' };

  // ignoreVary matters: servers commonly send `Vary: Accept-Encoding`, and cache
  // matching honours Vary by default - so a lookup by bare URL silently misses
  // a response that is definitely stored.
  const matches = await Promise.all(
    urls.map((url) => caches.match(url, { ignoreVary: true })),
  );
  const missing = matches.filter((match) => !match).length;

  return {
    id,
    // Partly-present means a newer build replaced the hashed files this pack
    // recorded. Saying "stale" is more useful to a teacher than "ready".
    status: missing === 0 ? 'ready' : missing === urls.length ? 'unavailable' : 'stale',
  };
}

export async function readPackStatus(prototype: Prototype): Promise<PackState> {
  if (!serviceWorkerSupported() || !prototype.load) {
    return { id: prototype.id, status: 'unavailable' };
  }
  return packState(prototype.id, readPackUrls(prototype.id));
}

export async function clearPacks(prototypes: Prototype[]): Promise<void> {
  const registration = await navigator.serviceWorker?.getRegistration();
  registration?.active?.postMessage({ type: 'CLEAR_PACKS' });

  for (const prototype of prototypes) {
    try {
      localStorage.removeItem(storageKey(prototype.id));
    } catch {
      // Nothing to do; the cache deletion above is what matters.
    }
  }
}

export async function storageEstimate(): Promise<string | null> {
  if (!navigator.storage?.estimate) return null;
  try {
    const { usage } = await navigator.storage.estimate();
    if (typeof usage !== 'number') return null;
    return `${(usage / 1024 / 1024).toFixed(1)} MB`;
  } catch {
    return null;
  }
}

/**
 * Captures Android's install prompt so it can be offered from our own button.
 *
 * The event fires once, early, and cannot be re-requested - so it has to be
 * caught at startup and held. Section 6 wants us to count the steps from
 * "new device" to "playable"; owning the prompt is what makes that countable.
 */
export interface InstallPrompt {
  available(): boolean;
  show(): Promise<'accepted' | 'dismissed' | 'unavailable'>;
  onChange(listener: () => void): void;
}

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export function createInstallPrompt(): InstallPrompt {
  let deferred: BeforeInstallPromptEvent | null = null;
  const listeners: (() => void)[] = [];
  const notify = () => listeners.forEach((listener) => listener());

  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault();
    deferred = event as BeforeInstallPromptEvent;
    notify();
  });

  window.addEventListener('appinstalled', () => {
    deferred = null;
    notify();
  });

  return {
    available: () => deferred !== null,

    async show() {
      if (!deferred) return 'unavailable';
      await deferred.prompt();
      const { outcome } = await deferred.userChoice;
      deferred = null;
      notify();
      return outcome;
    },

    onChange(listener) {
      listeners.push(listener);
    },
  };
}

export function isInstalled(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.matchMedia('(display-mode: fullscreen)').matches
  );
}
